import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { dismissFromArchivePatch, ARCHIVE_DISMISS_COLUMN } from "@/lib/venue-dismissal";
import { confirmShiftCompletion } from "@/lib/payments.functions";
import { submitReview } from "@/lib/reviews.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TipAllocationDialog } from "@/components/TipAllocationDialog";
import { toast } from "sonner";

type ShiftStatus = "open" | "awaiting_payment" | "paid" | "filled" | "completed" | "cancelled" | "disputed";

type Shift = {
  id: string;
  start_time: string;
  end_time: string;
  hourly_rate: number;
  role_type: string;
  status: ShiftStatus;
  total_cost: number | null;
  notes: string | null;
  bartender_id: string | null;
  payment_status: string;
  venue_confirmed_at: string | null;
  bartender_confirmed_at: string | null;
  address: string | null;
  postcode: string | null;
  positions_needed: number;
  tip_payment_status: "pending" | "paid" | "not_applicable" | null;
  tip_amount_pence: number | null;
  venue_archive_dismissed_at?: string | null;
};


type BartenderLite = { id: string; full_name: string | null; is_verified: boolean };

export const Route = createFileRoute("/_authenticated/venue/previous")({
  head: () => ({ meta: [{ title: "Previous Engagements — The Hospitality Reserve" }] }),
  component: PreviousEngagementsPage,
});

const STATUS_LABELS: Record<ShiftStatus, { label: string; tone: string }> = {
  open:              { label: "Unfilled",            tone: "text-muted-foreground" },
  awaiting_payment:  { label: "Payment Outstanding", tone: "text-destructive" },
  paid:              { label: "Awaiting Confirmation", tone: "text-accent" },
  filled:            { label: "Filled",              tone: "text-muted-foreground" },
  completed:         { label: "Completed",           tone: "text-foreground" },
  cancelled:         { label: "Cancelled",           tone: "text-muted-foreground" },
  disputed:          { label: "Disputed",            tone: "text-destructive" },
};

function PreviousEngagementsPage() {
  const navigate = useNavigate();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [bartenders, setBartenders] = useState<Record<string, BartenderLite>>({});
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [reviewShiftId, setReviewShiftId] = useState<string | null>(null);
  const [tipShiftId, setTipShiftId] = useState<string | null>(null);

  const confirmFn = useServerFn(confirmShiftCompletion);
  const reviewFn = useServerFn(submitReview);

  const load = async () => {
    setLoading(true);
    const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
    if (!u.user) {
      navigate({ to: "/auth", replace: true });
      return;
    }
    // Previous engagements = anything completed or cancelled. Active shifts
    // (paid/open/awaiting_payment/filled) remain on /venue until the venue
    // confirms completion.
    const { data } = await supabase
      .from("shifts")
      .select("id,start_time,end_time,hourly_rate,role_type,status,total_cost,notes,bartender_id,payment_status,venue_confirmed_at,bartender_confirmed_at,address,postcode,positions_needed,tip_payment_status,tip_amount_pence,venue_archive_dismissed_at")
      .in("status", ["completed", "cancelled"])
      .is(ARCHIVE_DISMISS_COLUMN, null)
      .order("end_time", { ascending: false });
    const list = (data ?? []) as Shift[];
    setShifts(list);


    const bartenderIds = Array.from(new Set(list.map((s) => s.bartender_id).filter(Boolean) as string[]));
    if (bartenderIds.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("id,full_name,is_verified")
        .in("id", bartenderIds);
      const map: Record<string, BartenderLite> = {};
      (profs ?? []).forEach((p) => { map[p.id] = p as BartenderLite; });
      setBartenders(map);
    }

    const shiftIds = list.map((s) => s.id);
    if (shiftIds.length) {
      const { data: revs } = await supabase.from("reviews").select("shift_id").in("shift_id", shiftIds);
      setReviewedIds(new Set((revs ?? []).map((r) => r.shift_id as string)));
    }
    setLoading(false);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (shiftId: string) => {
    setConfirmingId(shiftId);
    try {
      await confirmFn({ data: { shift_id: shiftId } });
      toast.success("Engagement confirmed.");
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not confirm completion.");
    } finally {
      setConfirmingId(null);
    }
  };

  const handleDismiss = async (shiftId: string) => {
    setShifts((prev) => prev.filter((s) => s.id !== shiftId));
    const { error } = await supabase
      .from("shifts")
      .update(dismissFromArchivePatch())
      .eq("id", shiftId);
    if (error) {
      toast.error("Could not remove that engagement.");
      void load();
    } else {
      toast.success("Engagement removed from your archive.");
    }
  };



  return (
    <div className="min-h-dvh bg-background text-foreground">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Archive</p>
            <h1 className="mt-3 font-display text-3xl lg:text-4xl">Previous Engagements</h1>
            <span className="gold-divider mt-4" />
            <p className="mt-4 text-sm text-muted-foreground max-w-xl">
              Every engagement whose scheduled end has passed. Confirm completion,
              leave reviews and reconcile tips here.
            </p>
          </div>
          <Link
            to="/venue"
            className="inline-block border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:border-foreground hover:text-foreground transition-colors"
          >
            ← Current Register
          </Link>
        </div>

        <div className="mt-10 space-y-4">
          {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {!loading && shifts.length === 0 && (
            <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              No previous engagements yet.
            </div>
          )}
          {shifts.map((s) => {
            const bartender = s.bartender_id ? bartenders[s.bartender_id] : undefined;
            const start = new Date(s.start_time);
            const end = new Date(s.end_time);
            const statusInfo = STATUS_LABELS[s.status] ?? STATUS_LABELS.open;
            const canConfirm = s.status === "paid" && !s.venue_confirmed_at;
            const canReview = s.status === "completed" && s.bartender_id && !reviewedIds.has(s.id);
            const tipPending = s.tip_payment_status === "pending" && s.status === "completed";

            return (
              <article key={s.id} className="relative border border-border bg-white p-6 pr-16 text-foreground">
                <button
                  type="button"
                  onClick={() => handleDismiss(s.id)}
                  title="Remove from archive"
                  className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-lg leading-none text-muted-foreground transition-colors hover:border-destructive hover:bg-destructive hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  <span aria-hidden>×</span>
                  <span className="sr-only">Remove this engagement from Previous Engagements</span>
                </button>
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div className="min-w-0 space-y-1">

                    <p className={`tracking-wide-luxury text-xs ${statusInfo.tone}`}>{statusInfo.label}</p>
                    <h2 className="mt-2 font-display text-xl capitalize">
                      {s.role_type.replace(/_/g, " ")} · {start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      {" – "}
                      {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      {s.address ? ` · ${s.address}` : ""}
                      {s.postcode ? ` · ${s.postcode}` : ""}
                    </p>
                  </div>
                  <div className="shrink-0 space-y-1 text-right text-sm">
                    <p className="font-medium">£{Number(s.hourly_rate).toFixed(2)}/hr</p>

                    {s.total_cost != null && (
                      <p className="text-muted-foreground">Total £{Number(s.total_cost).toFixed(2)}</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <p className="text-muted-foreground">
                    {bartender?.full_name
                      ? <>Bartender: <span className="text-foreground">{bartender.full_name}</span>{bartender.is_verified ? " · Verified" : ""}</>
                      : s.bartender_id
                        ? "Bartender assigned"
                        : "No bartender was confirmed"}
                  </p>
                  {tipPending && (
                    <span className="text-xs tracking-wide-luxury text-accent">Tip allocation pending</span>
                  )}
                </div>

                {(canConfirm || canReview || tipPending) && (
                  <div className="mt-5 flex flex-wrap gap-2">
                    {canConfirm && (
                      <Button
                        type="button"
                        onClick={() => handleConfirm(s.id)}
                        disabled={confirmingId === s.id}
                        className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury"
                      >
                        {confirmingId === s.id ? "Confirming…" : "Confirm Completion"}
                      </Button>
                    )}
                    {tipPending && (
                      <Button
                        type="button"
                        onClick={() => setTipShiftId(s.id)}
                        className="rounded-none bg-accent text-accent-foreground hover:opacity-90 tracking-wide-luxury"
                      >
                        Allocate Tips
                      </Button>
                    )}
                    {canReview && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setReviewShiftId(s.id)}
                        className="rounded-none tracking-wide-luxury"
                      >
                        Leave Review
                      </Button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </main>

      <ReviewDialog
        shiftId={reviewShiftId}
        onClose={() => setReviewShiftId(null)}
        onSubmitted={() => { setReviewShiftId(null); void load(); }}
        submit={reviewFn}
      />

      {tipShiftId && (
        <TipAllocationDialog
          shiftId={tipShiftId}
          bartenderName={
            shifts.find((s) => s.id === tipShiftId)?.bartender_id
              ? bartenders[shifts.find((s) => s.id === tipShiftId)!.bartender_id!]?.full_name ?? null
              : null
          }
          onClose={() => setTipShiftId(null)}
          onAllocated={() => { setTipShiftId(null); void load(); }}
        />
      )}
    </div>
  );
}

function ReviewDialog({
  shiftId, onClose, onSubmitted, submit,
}: {
  shiftId: string | null;
  onClose: () => void;
  onSubmitted: () => void;
  submit: (args: { data: { shift_id: string; rating: number; comment?: string } }) => Promise<unknown>;
}) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (shiftId) { setRating(5); setComment(""); }
  }, [shiftId]);

  const handle = async () => {
    if (!shiftId) return;
    setBusy(true);
    try {
      await submit({ data: { shift_id: shiftId, rating, comment: comment.trim() || undefined } });
      toast.success("Review submitted.");
      onSubmitted();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not submit review.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!shiftId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Leave a Review</DialogTitle>
          <DialogDescription>Your reflection helps maintain the standard of the register.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs tracking-wide-luxury text-muted-foreground">Rating</p>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className={`h-9 w-9 border text-sm ${rating >= n ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs tracking-wide-luxury text-muted-foreground">Comment (optional)</p>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} className="mt-2 rounded-none" />
          </div>
          <Button
            type="button"
            onClick={handle}
            disabled={busy}
            className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6"
          >
            {busy ? "Submitting…" : "Submit Review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
